import { useMemo, useCallback } from 'react';
import {
  useGetGridStateQuery,
  useSaveGridStateMutation,
  useRemoveGridStateMutation,
} from '../api/gridStateApi';
import type { PersistedGridState } from '../types';

type UseGridStatePersistenceReturn = {
  savedState: PersistedGridState | null;
  isLoading: boolean;
  isFetching: boolean;
  saveState: (state: PersistedGridState) => void;
  clearState: () => void;
};

/**
 * Hook that loads, saves, and clears persisted grid state (column visibility,
 * sorting, column sizing, page size) via the GridState backend API.
 *
 * @param contentId - The numeric grid identifier (GridMetadata.contentId). Pass
 *   undefined/null to skip all API calls (the hook becomes a no-op).
 */
export const useGridStatePersistence = (
  contentId: number | undefined | null
): UseGridStatePersistenceReturn => {
  const skip = contentId == null;

  const {
    data: rawJson,
    isLoading,
    isFetching,
  } = useGetGridStateQuery({ gridId: contentId! }, { skip });

  const [saveGridState] = useSaveGridStateMutation();
  const [removeGridState] = useRemoveGridStateMutation();

  const savedState = useMemo<PersistedGridState | null>(() => {
    if (!rawJson) return null;
    try {
      return JSON.parse(rawJson) as PersistedGridState;
    } catch {
      return null;
    }
  }, [rawJson]);

  const saveState = useCallback(
    (state: PersistedGridState) => {
      if (skip) return;
      saveGridState({ gridId: contentId!, options: JSON.stringify(state) })
        .unwrap()
        .catch((err) => console.error('Failed to save grid state:', err));
    },
    [skip, contentId, saveGridState]
  );

  const clearState = useCallback(() => {
    if (skip) return;
    removeGridState({ gridId: contentId! })
      .unwrap()
      .catch((err) => console.error('Failed to remove grid state:', err));
  }, [skip, contentId, removeGridState]);

  return { savedState, isLoading, isFetching, saveState, clearState };
};
